/**
 * Owner: Masa Bayu
 * File: AdminOrdersScreen.tsx
 * Date:Time: 2026-10-23 10:45
 * Description: §467-D — ledger pesanan penuh. Tapisan dan paginasi dijalankan DI
 *   HUB (`GET /v1/admin/orders`, §467d-R); klien hanya menghantar parameter dan
 *   memaparkan `total`/`stateCounts` yang dibalut pelayan, jadi "menunjukkan
 *   X – Y daripada Z" tak pernah dikarang. Tiada satu pun kawalan menulis: order
 *   dipantau, tidak diubah (harga pula hanya masuk semasa Publish).
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { AdminLabels } from './admin-labels';
import type { AdminDashboardView } from './admin-dashboard';
import { buildOrdersQuery, useAdminOrders, type AdminOrderFilters } from './admin-orders';
import {
  NO_DATA,
  PAGE_SIZE,
  dateTimeText,
  moneyText,
  pageRange,
  stateLabel,
  stateTone,
} from './admin-view';
import { AdminMoneyList } from './AdminMoneyList';
import { Icon } from './AdminIcons';

/** Keadaan yang dikenal UI; senarai sebenar dicantum dengan kiraan hub. */
const KNOWN_STATES = ['paid', 'pending', 'failed', 'refunded'] as const;

interface Draft {
  state: string;
  publicationId: string;
  accountId: string;
  orderId: string;
}

const BLANK: Draft = { state: '', publicationId: '', accountId: '', orderId: '' };

export interface AdminOrdersScreenProps {
  readonly view: AdminDashboardView;
  readonly la: AdminLabels;
}

export function AdminOrdersScreen({ view, la }: AdminOrdersScreenProps): ReactElement {
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [applied, setApplied] = useState<Draft>(BLANK);
  const [offset, setOffset] = useState(0);

  const filters = useMemo<AdminOrderFilters>(
    () => ({
      state: applied.state === '' ? undefined : applied.state,
      publicationId: applied.publicationId === '' ? undefined : applied.publicationId,
      accountId: applied.accountId === '' ? undefined : applied.accountId,
      orderId: applied.orderId === '' ? undefined : applied.orderId,
      limit: PAGE_SIZE,
      offset,
    }),
    [applied, offset],
  );

  const orders = useAdminOrders(filters);
  const page = orders.page;
  const range = page === null ? { from: 0, to: 0 } : pageRange(page);
  const query = buildOrdersQuery({ ...filters });

  const states = useMemo(() => {
    const keys = new Set<string>(KNOWN_STATES);
    for (const key of Object.keys(view.counts.orders.byState)) keys.add(key);
    if (page !== null) for (const key of Object.keys(page.stateCounts)) keys.add(key);
    return [...keys];
  }, [view.counts.orders.byState, page]);

  const apply = (): void => {
    setApplied(draft);
    setOffset(0);
  };
  const clear = (): void => {
    setDraft(BLANK);
    setApplied(BLANK);
    setOffset(0);
  };

  const counts = page?.stateCounts ?? view.counts.orders.byState;
  const canPrev = offset > 0;
  const canNext = page !== null && offset + page.returned < page.total;

  return (
    <div className="qxa__stack">
      <section className="qxa__card qxa__filters-card" aria-label={la.ordersTitle}>
        <div className="qxa__filters">
          <label className="qxa__field">
            <span className="qxa__field-label">{la.filterState}</span>
            <select
              value={draft.state}
              onChange={(event) => setDraft({ ...draft, state: event.target.value })}
            >
              <option value="">{la.none}</option>
              {states.map((key) => (
                <option key={key} value={key}>
                  {stateLabel(key, la)}
                </option>
              ))}
            </select>
          </label>

          <label className="qxa__field">
            <span className="qxa__field-label">{la.filterPublication}</span>
            <select
              value={draft.publicationId}
              onChange={(event) => setDraft({ ...draft, publicationId: event.target.value })}
            >
              <option value="">{la.none}</option>
              {view.publications.map((book) => (
                <option key={book.publicationId} value={book.publicationId}>
                  {book.title}
                </option>
              ))}
            </select>
          </label>

          <label className="qxa__field">
            <span className="qxa__field-label">{la.filterAccount}</span>
            <input
              type="text"
              value={draft.accountId}
              placeholder={la.searchPlaceholder}
              onChange={(event) => setDraft({ ...draft, accountId: event.target.value })}
            />
          </label>

          <label className="qxa__field">
            <span className="qxa__field-label">{la.filterOrderId}</span>
            <input
              type="text"
              value={draft.orderId}
              placeholder={la.searchPlaceholder}
              onChange={(event) => setDraft({ ...draft, orderId: event.target.value })}
            />
          </label>
        </div>

        <div className="qxa__head-tools">
          <button type="button" className="qxa__btn qxa__btn--primary" onClick={apply}>
            {la.filterApply}
          </button>
          <button type="button" className="qxa__btn" onClick={clear}>
            {la.filterReset}
          </button>
          <button type="button" className="qxa__btn qxa__btn--ghost" onClick={orders.reload}>
            <Icon name="refresh" size={16} />
            <span>{la.refresh}</span>
          </button>
        </div>
        <code className="qxa__query">{`GET /v1/admin/orders${query}`}</code>
      </section>

      <ul className="qxa__counts" aria-label={la.stateCountsAria}>
        {Object.entries(counts).map(([key, value]) => (
          <li key={key}>
            <span className={`qxa__pill qxa__pill--${stateTone(key)}`}>
              {stateLabel(key, la)} · {String(value)}
            </span>
          </li>
        ))}
      </ul>

      <section className="qxa__card" aria-label={la.ordersTitle}>
        {orders.loading ? <p className="qxa__empty">{la.loadingOrders}</p> : null}
        {orders.error !== null ? <p className="qxa__error">{orders.error}</p> : null}
        {!orders.loading && orders.error === null && page !== null && page.items.length === 0 ? (
          <p className="qxa__empty">{la.ordersEmpty}</p>
        ) : null}

        {page !== null && page.items.length > 0 ? (
          <>
            <div className="qxa__table-wrap">
              <table className="qxa__table">
                <caption className="qxa__sr">{la.ordersTitle}</caption>
                <thead>
                  <tr>
                    <th scope="col">{la.colOrder}</th>
                    <th scope="col">{la.colTitle}</th>
                    <th scope="col">{la.colAmount}</th>
                    <th scope="col">{la.colState}</th>
                    <th scope="col">{la.colCreated}</th>
                    <th scope="col">{la.colPaid}</th>
                    <th scope="col">{la.colBuyer}</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((order) => (
                    <tr key={order.orderId}>
                      <th scope="row" data-label={la.colOrder}>
                        <code>{order.orderId}</code>
                      </th>
                      <td data-label={la.colTitle}>
                        <span className="qxa__cell-title">{order.title}</span>
                        <code className="qxa__row-id">{order.publicationId}</code>
                      </td>
                      <td data-label={la.colAmount}>
                        {moneyText(order.amountMinorUnits, order.currency, la.noData)}
                      </td>
                      <td data-label={la.colState}>
                        <span className={`qxa__pill qxa__pill--${stateTone(order.state)}`}>
                          {stateLabel(order.state, la)}
                        </span>
                      </td>
                      <td data-label={la.colCreated}>{dateTimeText(order.createdAt)}</td>
                      <td data-label={la.colPaid}>{dateTimeText(order.paidAt)}</td>
                      <td data-label={la.colBuyer}>{order.buyerEmail ?? NO_DATA}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="qxa__pager">
              <p className="qxa__range">
                {la.showing} {String(range.from)} – {String(range.to)} {la.of} {String(page.total)}
              </p>
              <div className="qxa__head-tools">
                <button
                  type="button"
                  className="qxa__btn"
                  disabled={!canPrev}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  <Icon name="arrowLeft" size={16} />
                  <span>{la.pagePrev}</span>
                </button>
                <button
                  type="button"
                  className="qxa__btn"
                  disabled={!canNext}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  <span>{la.pageNext}</span>
                  <Icon name="arrowRight" size={16} />
                </button>
              </div>
            </div>

            <div className="qxa__page-revenue">
              <h3 className="qxa__card-title">{la.kpiRevenue}</h3>
              <AdminMoneyList rows={page.revenueByCurrency} la={la} />
              <p className="qxa__note">{la.revenuePerCurrency}</p>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}
